import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiLink,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
} from "@inscopix/ideas-eui";
import {
  ALL_CONTAINERS_QUERY_KEY,
  ContainerDjango,
} from "./ContainerBrowser.api";
import { getEnvVar } from "ideas.env";
import { getRequestHeaders } from "utils/getRequestHeaders";
import axios from "axios";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Deletes a new container image.
 * @param toolId
 */
const deleteContainer = async (containerId: number) => {
  const url = getEnvVar("URL_TES_CONTAINER_DELETE")(containerId);
  const headers = await getRequestHeaders();
  const body = { date_deleted: new Date().toISOString() };
  await axios.patch(url, body, { headers });
};

interface ModalDeleteContainerProps {
  id: ContainerDjango["id"];
  tools: ContainerDjango["tools"];
  onClose: () => void;
}

export const ModalDeleteContainer = ({
  id,
  tools,
  onClose,
}: ModalDeleteContainerProps) => {
  const client = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await deleteContainer(id);
      await client.invalidateQueries([ALL_CONTAINERS_QUERY_KEY]);
      addUtilityToastSuccess("Container deleted");
      onClose();
    } catch (error) {
      addUtilityToastFailure("Failed to delete container");
      setIsLoading(false);
    }
  };

  return (
    <EuiModal onClose={onClose} style={{ width: 800 }}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>Delete Container</EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        {tools.length === 0 ? (
          <p>Are you sure you want to delete this container?</p>
        ) : (
          <EuiCallOut
            title="Container used by published tools"
            color="warning"
            iconType="warning"
          >
            <p>
              This container is being used by the following published tools:
              <ul>
                {tools.map((tool) => (
                  <li key={tool.id}>
                    <EuiLink target="_blank">{tool.name}</EuiLink>
                  </li>
                ))}
              </ul>
            </p>

            <p>
              Deleting this container will deprecate these tools. Do you want to
              proceed?
            </p>
          </EuiCallOut>
        )}
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>

        <EuiButton
          isLoading={isLoading}
          fill
          color="danger"
          onClick={() => void handleConfirm()}
        >
          Delete
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
